// ===========================================================================
// The staff inbox: a conversation between Lumi Staff and one person, kept apart
// from the messages regulars send each other.
//
// Messages between regulars (server messages.js) are for two people who both
// waved, in ONE band, behind a per-band switch. A message from staff is none of
// those: staff are not a regular, not in a band, and the point of one is often
// to reach somebody whom the band rules, a switch or a mismatch has cut off from
// the very thing being explained to them. So this is its own channel, with its
// own store, and it reaches a person in either band whatever the social and
// messages switches say.
//
// Who may be written to is decided on the server (staff-inbox.js there) and is
// deliberately narrow: somebody Lumi can call something -- an account's
// username, a name put on a keepsake to save regulars, or a nickname that was
// saved when they waved. A person who is anonymous in every sense is not
// listed and cannot be written to. A person can only ever REPLY: nothing here
// lets anyone start a conversation with staff.
//
// The wire shapes below are what the three packages agree on. ASCII only.
// ===========================================================================

/** The id the thread view uses for the other end of this conversation (a regular's row has a person id here). */
export const STAFF_PERSON = 'staff:lumi';
/** What the other end is called to the person. */
export const STAFF_NAME = 'Lumi Staff';
/** The longest message either side may send. */
export const STAFF_INBOX_MAX_CHARS = 1000;
/** A message is gone this long after it was written, as every private message on Lumi is. */
export const STAFF_INBOX_TTL_DAYS = 30;

/**
 * Why a person can be written to, strongest first. This order IS the order the
 * console lists them in: accounts, then people who put a username on a device to
 * keep their regulars, then people known only by a nickname saved when they
 * waved.
 */
export const StaffTier = Object.freeze({
  ACCOUNT: 'account',
  USERNAME: 'username',
  NICKNAME: 'nickname',
});
export const STAFF_TIER_ORDER = Object.freeze([StaffTier.ACCOUNT, StaffTier.USERNAME, StaffTier.NICKNAME]);

/** Whether a value is one of the tiers. */
export function isStaffTier(t) {
  return STAFF_TIER_ORDER.includes(t);
}

/**
 * HTTP, for the person (person auth as /api/regulars/list: an account bearer
 * and/or the X-Lumi-Keepsake header; no socket and no band switch needed).
 *
 *   GET  /api/staff-inbox?peek=1
 *        -> { count, unread, lastAt }              does not mark anything seen
 *   GET  /api/staff-inbox
 *        -> { count, unread: 0, lastAt, messages: [UserMessage] }   marks them seen
 *   POST /api/staff-inbox  { text, id? }
 *        -> { message: UserMessage }               409 { error: 'no_thread' } when staff never wrote
 *   POST /api/staff-inbox/hide  { id }
 *        -> { ok: true, id }                        the person puts a line away; a line from staff only
 *                                                   once read (409 { error: 'not_read' }), 404 otherwise.
 *        A hidden line is gone from the person's view and counts, and stays in the console's.
 *
 * UserMessage: { id, from: 'staff' | 'me', text, at }   (at is ISO)
 *
 * Live: ServerMsg.STAFF_MSG, { id, from: 'staff', text, at }, to every open tab.
 *
 * HTTP, for the console (admin token), under /api/admin/staff-inbox:
 *
 *   GET  /people?q=           -> { conversations: [Conversation], people: [Reachable] }
 *   GET  /thread/<personId>   -> { person: Reachable, messages: [StaffMessage] }   marks the replies read
 *   POST /thread/<personId>/message  { text }  -> { message: StaffMessage }
 *                                                  404 { error: 'not_reachable' } for somebody not in the list
 *
 * Reachable:    { personId, name, tier, band: 'adult'|'minor'|null, hasThread, lastSeenAt: ISO|null }
 *               lastSeenAt: when the device (or, for an account, the newest device that signed in
 *               to it) was last on the site, to the hour; same-name people are listed newest first
 * Conversation: Reachable & { unread, lastAt, lastFrom: 'staff'|'person', preview, count }
 * StaffMessage: { id, from: 'staff' | 'person', text, at, seen, hidden }   seen: the other end has opened it;
 *               hidden: the person put it away after reading it
 *
 * The overview carries `staffReplies` (replies the console has not opened) and
 * `staffRepliesLatest` (ISO of the newest), and the 'staffinbox' admin-bus
 * channel carries { signature } and changes whenever either end writes or reads.
 */
export default { STAFF_PERSON, STAFF_NAME, STAFF_INBOX_MAX_CHARS, STAFF_INBOX_TTL_DAYS, StaffTier, STAFF_TIER_ORDER, isStaffTier };
